import { Suspense, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { RelayEnvironmentProvider } from 'react-relay';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { WorkspaceProvider, useWorkspace } from '@/contexts/WorkspaceContext';
import { createRelayEnvironment } from '@/relay/environment';
import { Layout } from '@/components/layout/Layout';
import { LoadingState } from '@/components/loading/LoadingState';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { RegisterPage } from '@/features/auth/pages/RegisterPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { ProjectsPage } from '@/features/projects/pages/ProjectsPage';
import { ReportsPage } from '@/features/reports/pages/ReportsPage';
import { DaySheetlyPage } from '@/features/daySheetly/pages/DaySheetlyPage';
import { OnboardingWorkspacePage } from '@/features/workspace/pages/OnboardingWorkspacePage';
import { WorkspaceMembersPage } from '@/features/workspace/pages/WorkspaceMembersPage';
import { AcceptInvitePage } from '@/features/workspace/pages/AcceptInvitePage';
import { ActivityAssistantSettingsPage } from '@/features/activityAssistant/pages/ActivityAssistantSettingsPage';
import { ActivityReviewPage } from '@/features/activityAssistant/pages/ActivityReviewPage';
import { SubscriptionPage } from '@/features/billing/pages/SubscriptionPage';
import { AccountSettingsPage } from '@/features/account/pages/AccountSettingsPage';
import { McpIntegrationPage } from '@/features/mcp/pages/McpIntegrationPage';
import { MetaPixel } from '@/components/MetaPixel';
import { PostHogAnalytics } from '@/components/PostHogAnalytics';
import { TrackingConsentBanner } from '@/components/TrackingConsentBanner';

function WorkspaceScopedLayout() {
  const { activeWorkspaceId } = useWorkspace();

  // Remount the layout and all route content when the active workspace
  // changes, clearing local component state such as dialogs and filters.
  return <Layout key={activeWorkspaceId ?? 'no-active-workspace'} />;
}

function RelayScopedApp() {
  const { activeWorkspaceId } = useAuth();
  const environment = useMemo(
    () => createRelayEnvironment(),
    [activeWorkspaceId],
  );

  return (
    <RelayEnvironmentProvider environment={environment}>
      <BrowserRouter>
        <Suspense fallback={<LoadingState className="min-h-screen" message="Loading Day Sheetly..." />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/onboarding/workspace" element={<OnboardingWorkspacePage />} />
              <Route element={<WorkspaceProvider />}>
                <Route element={<WorkspaceScopedLayout />}>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/daysheetly" element={<DaySheetlyPage />} />
                  <Route path="/day-sheetly" element={<Navigate to="/daysheetly" replace />} />
                  <Route path="/time-tracking" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/activities/review" element={<ActivityReviewPage />} />
                  <Route
                    path="/settings/activity-assistant"
                    element={<ActivityAssistantSettingsPage />}
                  />
                  <Route path="/settings/subscription" element={<SubscriptionPage />} />
                  <Route path="/settings/account" element={<AccountSettingsPage />} />
                  <Route path="/settings/integrations/mcp" element={<McpIntegrationPage />} />
                  <Route
                    path="/workspace/:id/settings/members"
                    element={<WorkspaceMembersPage />}
                  />
                </Route>
              </Route>
            </Route>
          </Routes>
        </Suspense>
        <Toaster richColors closeButton position="top-right" />
      </BrowserRouter>
    </RelayEnvironmentProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <MetaPixel />
      <PostHogAnalytics />
      <RelayScopedApp />
      <TrackingConsentBanner />
    </AuthProvider>
  );
}
